import React, { useState, useEffect } from "react";
import "../styles/DownloadsFormularePage.css";
import { getWissenswertPage, getDownloads, getDownloadCategories } from "../lib/api";
import { Page, PageHeader, FilterChips } from "../components/ui";

export default function DownloadsFormularePage() {
  const [page, setPage] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Alle");

  useEffect(() => {
    Promise.all([getWissenswertPage(), getDownloads(), getDownloadCategories()])
      .then(([pageRes, downloadsRes, catsRes]) => {
        setPage(pageRes);
        setItems(downloadsRes);
        setCategories(catsRes);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      });
  }, []);

  const featuredItems = items.filter((item) => item.featured);

  const filteredItems =
    activeCategory === "Alle"
      ? items.filter((item) => !item.featured)
      : items.filter((item) => !item.featured && item.category === activeCategory);

  const grouped = categories.reduce((acc, cat) => {
    const catItems = filteredItems.filter((item) => item.category === cat.name);
    if (catItems.length > 0) acc.push({ title: cat.name, items: catItems });
    return acc;
  }, []);

  // Also show ungrouped items that don't match any category
  if (activeCategory !== "Alle") {
    const direct = filteredItems.filter((item) => item.category === activeCategory);
    if (grouped.length === 0 && direct.length > 0) {
      grouped.push({ title: activeCategory, items: direct });
    }
  }

  return (
    <Page className="df-page" loading={!page} error={error}>
      <PageHeader
        parent={{ label: "Eltern & Schule", to: "/mitgestalten" }}
        current={page?.heroTitle || "Downloads & Formulare"}
        title={page?.heroTitle || "Downloads & Formulare"}
        subtitle={page?.heroSubtitle}
      />

      <section className="df-section">
        <div className="container">
          <FilterChips
            options={[{ value: "Alle", label: page?.allCategoriesLabel || "Alle" }, ...categories]}
            value={activeCategory}
            showAll={false}
            label="Kategorien"
            onChange={setActiveCategory}
          />

          <div className="df-layout">
            <div className="df-main">
              {grouped.map((group) => (
                <SectionBlock
                  key={group.title}
                  title={group.title}
                  items={group.items}
                  pendingFileText={page?.pendingFileText}
                />
              ))}
              {grouped.length === 0 && (
                <div style={{ padding: "40px 0", color: "rgba(16,17,20,0.5)", textAlign: "center" }}>
                  {page?.emptyStateText || "Keine Einträge in dieser Kategorie."}
                </div>
              )}
            </div>

            <aside className="df-side" aria-label="Wichtige Konzepte">
              {featuredItems.length > 0 ? (
                <div className="df-sideCard">
                  <p className="df-sideCard__kicker">{page?.sideCardKicker || "Wichtige Konzepte"}</p>
                  <h2>{page?.sideCardTitle || "Besonders relevant"}</h2>
                  <div className="df-featured-list">
                    {featuredItems.map((item) => (
                      <a
                        className="df-featured"
                        key={item.id}
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className="df-featured__icon" aria-hidden="true">
                          <IconDocument />
                        </div>
                        <div>
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                          {item.fileSize ? <span>PDF · {item.fileSize}</span> : null}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="df-sideMini">
                <div className="df-sideMini__icon" aria-hidden="true">
                  <IconMail />
                </div>
                <div>
                  <strong>{page?.contactTitle || "Fragen zu Unterlagen?"}</strong>
                  <p>{page?.contactText || "Bei Fragen zu Formularen oder Dokumenten schreiben Sie uns gerne direkt."}</p>
                  {page?.contactEmail ? (
                    
                    <button
                    type="button"
                    className="faqp-btn faqp-btn--primary"
                    onClick={() => {
                      window.location.href = `mailto:${page?.contactEmail || "Direktion.912081@schule.wien.gv.at"}`;
                    }}
                  >
                    Mail schreiben
                  </button>
                  ) : null}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </Page>
  );
}

function SectionBlock({ title, items, pendingFileText }) {
  return (
    <section className="df-block">
      <div className="df-block__head">
        <h2>{title}</h2>
      </div>
      <div className="df-files">
        {items.map((item) => (
          item.href && item.href !== "#" ? (
            <a
              className="df-file"
              key={item.id}
              href={item.href}
              target="_blank"
              rel="noreferrer"
            >
              <div className="df-file__icon" aria-hidden="true"><IconFile /></div>
              <div className="df-file__body">
                <div className="df-file__top">
                  <strong>{item.title}</strong>
                  {item.fileSize ? <span>PDF · {item.fileSize}</span> : null}
                </div>
                <p>{item.description}</p>
              </div>
              <div className="df-file__action" aria-hidden="true"><IconArrowRight /></div>
            </a>
          ) : (
            <div className="df-file df-file--pending" key={item.id}>
              <div className="df-file__icon" aria-hidden="true"><IconFile /></div>
              <div className="df-file__body">
                <div className="df-file__top">
                  <strong>{item.title}</strong>
                  <span style={{ color: "rgba(16,17,20,0.38)" }}>{pendingFileText || "Datei wird vorbereitet…"}</span>
                </div>
                <p>{item.description}</p>
              </div>
            </div>
          )
        ))}
      </div>
    </section>
  );
}

function Svg({ children, size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">{children}</svg>;
}
function IconDocument() {
  return <Svg><path d="M7 3h7l5 5v13H7V3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M10 13h6M10 17h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></Svg>;
}
function IconMail() {
  return <Svg><path d="M4 6h16v12H4V6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M4 8l8 6 8-6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></Svg>;
}
function IconFile() {
  return <Svg><path d="M7 3h7l5 5v13H7V3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></Svg>;
}
function IconArrowRight() {
  return <Svg><path d="M5 12h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M13 7l5 5-5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></Svg>;
}